import React from 'react';
import { MapPin, Clock, Wifi, Video, BarChart2, Instagram } from 'lucide-react';

const responsibilities = [
  'Create and post content for Instagram, TikTok, Facebook, and YouTube Shorts',
  'Film vehicles, client deliveries, pickups, and behind-the-scenes moments',
  'Edit short-form videos, Reels, Stories, photos, and graphics',
  'Write captions and calls-to-action that generate inquiries',
  'Maintain a consistent posting schedule and content calendar',
  'Respond to comments and engage with followers',
  'Monitor trends, sounds, and automotive content opportunities',
  'Showcase available, sourced, and delivered vehicles',
  'Create educational content on the benefits of an auto concierge',
  'Highlight customer testimonials and success stories',
  'Track views, engagement, follower growth, and leads',
  'Work with the team to develop promotions that drive new business',
];

const requirements = [
  'Proven experience with Instagram and TikTok content creation',
  'Familiarity with CapCut, Canva, Adobe Premiere, or similar tools',
  'Comfortable appearing on camera occasionally',
  'Able to take initiative — sees a great moment and films it',
  'Interest in cars strongly preferred (expertise not required)',
  'Creative, organised, and dependable — works well independently',
  'Understands what makes people stop scrolling',
];

const metrics = [
  { icon: <Video size={16} />,      label: 'Consistent Quality Content', desc: 'Regular, on-brand posts across all platforms' },
  { icon: <BarChart2 size={16} />,  label: 'Engagement & Reach',         desc: 'Growing views, likes, shares, and saves' },
  { icon: <Instagram size={16} />,  label: 'Audience Growth',            desc: 'Real followers who become real customers' },
];

export default function Careers() {
  return (
    <>
      {/* ── Hero: dark navy — matches HowItWorks / FindMyCar ── */}
      <section style={{ background: 'var(--navy-dark)', padding: '160px 24px 96px', position: 'relative', overflow: 'hidden' }}>
        <div style={{ position: 'absolute', inset: 0, opacity: 0.03, backgroundImage: 'linear-gradient(var(--gold) 1px, transparent 1px), linear-gradient(90deg, var(--gold) 1px, transparent 1px)', backgroundSize: '40px 40px' }} />

        <div style={{ maxWidth: 1200, margin: '0 auto', position: 'relative', zIndex: 2 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end', flexWrap: 'wrap', gap: 32 }}>
            <div style={{ maxWidth: 640 }}>
              <span className="section-label">We're Hiring</span>
              <span className="gold-line" style={{ display: 'block', marginTop: 12, marginBottom: 24 }} />
              <h1 style={{ fontFamily: 'Inter', fontSize: 'clamp(36px, 5vw, 60px)', fontWeight: 800, color: 'white', margin: '0 0 20px', lineHeight: 1.1 }}>
                Join the{' '}
                <span className="gold-shimmer">VeriDrive</span>{' '}
                Team
              </h1>
              <p style={{ fontSize: 17, color: 'rgba(255,255,255,0.5)', lineHeight: 1.75, maxWidth: 500, margin: 0 }}>
                We're building something different in the auto industry — and we need the right people to help tell that story.
              </p>
            </div>

            {/* Role stat */}
            <div style={{ background: 'rgba(201,168,76,0.08)', border: '1px solid rgba(201,168,76,0.2)', borderRadius: 12, padding: '28px 36px', textAlign: 'center' }}>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8, marginBottom: 8 }}>
                <span style={{ width: 8, height: 8, borderRadius: '50%', background: '#4ade80', display: 'inline-block' }} />
                <span style={{ fontSize: 11, fontWeight: 700, color: 'var(--gold)', letterSpacing: '0.1em', textTransform: 'uppercase' }}>Now Hiring</span>
              </div>
              <div style={{ fontSize: 40, fontWeight: 800, color: 'white', lineHeight: 1 }}>1</div>
              <div style={{ fontSize: 13, color: 'rgba(255,255,255,0.4)', marginTop: 4 }}>Open Role</div>
            </div>
          </div>
        </div>
      </section>

      {/* ── Job listing: cream — matches Services ── */}
      <section style={{ background: 'var(--cream)', padding: '80px 24px 100px' }}>
        <div style={{ maxWidth: 1200, margin: '0 auto' }}>

          {/* Card */}
          <div className="card-hover" style={{ background: 'white', border: '1px solid #E4EEF7', borderRadius: 12, overflow: 'hidden', boxShadow: '0 4px 24px rgba(8,27,75,0.06)' }}>

            {/* Card header — navy strip */}
            <div style={{ background: 'var(--navy-dark)', padding: '32px 40px', display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', flexWrap: 'wrap', gap: 20 }}>
              <div>
                <div style={{ fontSize: 11, fontWeight: 700, letterSpacing: '0.14em', textTransform: 'uppercase', color: 'var(--gold)', marginBottom: 10 }}>
                  Social Media & Content
                </div>
                <h2 style={{ fontSize: 'clamp(20px, 3vw, 28px)', fontWeight: 700, color: 'white', margin: '0 0 18px', lineHeight: 1.2 }}>
                  Social Media & Content Coordinator
                </h2>
                <div style={{ display: 'flex', gap: 20, flexWrap: 'wrap' }}>
                  {[
                    { icon: <Clock size={13} />, text: 'Part-Time / Full-Time' },
                    { icon: <Wifi size={13} />, text: 'Remote' },
                    { icon: <MapPin size={13} />, text: 'VeriDrive · veridrive.autos' },
                    { icon: <span style={{ fontSize: 12, fontWeight: 800 }}>₦</span>, text: '300,000 – 400,000 / month' },
                  ].map(({ icon, text }) => (
                    <div key={text} style={{ display: 'flex', alignItems: 'center', gap: 6, color: 'rgba(255,255,255,0.5)', fontSize: 13, fontWeight: 500 }}>
                      <span style={{ color: 'var(--gold)' }}>{icon}</span>
                      {text}
                    </div>
                  ))}
                </div>
              </div>

              <a
                href="mailto:veridriveus@gmail.com?subject=Application%20%E2%80%94%20Social%20Media%20%26%20Content%20Coordinator&body=Hi%20VeriDrive%20team%2C%0A%0AI'm%20applying%20for%20the%20Social%20Media%20%26%20Content%20Coordinator%20role.%0A%0APortfolio%2Flinks%3A%0A%0AWhy%20I'm%20interested%3A"
                className="btn-gold"
                style={{ whiteSpace: 'nowrap', alignSelf: 'flex-start' }}
              >
                Apply Now →
              </a>
            </div>

            {/* Card body — 3 columns matching Services grid density */}
            <div style={{ padding: '40px', display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 40, alignItems: 'start' }} className="careers-body">

              {/* Col 1: About + success metrics */}
              <div>
                <div style={{ fontSize: 11, fontWeight: 700, letterSpacing: '0.12em', textTransform: 'uppercase', color: 'var(--gold)', marginBottom: 12 }}>About the Role</div>
                <p style={{ fontSize: 14, lineHeight: 1.75, color: '#5A7A96', margin: '0 0 32px' }}>
                  VeriDrive is an automotive concierge that makes buying, sourcing, and exporting vehicles seamless. We need a creative Content Coordinator who can turn everyday moments in the business into content that builds trust and drives inquiries.
                </p>

                <div style={{ fontSize: 11, fontWeight: 700, letterSpacing: '0.12em', textTransform: 'uppercase', color: 'var(--gold)', marginBottom: 14 }}>What Success Looks Like</div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                  {metrics.map(({ icon, label, desc }) => (
                    <div key={label} style={{ display: 'flex', gap: 12, alignItems: 'flex-start', padding: '12px 14px', background: 'var(--slate)', borderRadius: 8 }}>
                      <div style={{ width: 32, height: 32, background: 'var(--navy-dark)', borderRadius: 7, display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'var(--gold)', flexShrink: 0 }}>
                        {icon}
                      </div>
                      <div>
                        <div style={{ fontSize: 12, fontWeight: 700, color: 'var(--navy-dark)', marginBottom: 2 }}>{label}</div>
                        <div style={{ fontSize: 11, color: '#9BAFC4', lineHeight: 1.5 }}>{desc}</div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Col 2: Responsibilities */}
              <div>
                <div style={{ fontSize: 11, fontWeight: 700, letterSpacing: '0.12em', textTransform: 'uppercase', color: 'var(--gold)', marginBottom: 14 }}>What You'll Do</div>
                <ul style={{ listStyle: 'none', padding: 0, margin: 0, display: 'flex', flexDirection: 'column', gap: 9 }}>
                  {responsibilities.map(r => (
                    <li key={r} style={{ display: 'flex', alignItems: 'flex-start', gap: 8, fontSize: 13, color: '#4A6A84', lineHeight: 1.65 }}>
                      <span style={{ color: 'var(--gold)', fontWeight: 700, fontSize: 15, lineHeight: 1.35, flexShrink: 0 }}>✓</span>
                      {r}
                    </li>
                  ))}
                </ul>
              </div>

              {/* Col 3: Requirements + apply CTA */}
              <div>
                <div style={{ fontSize: 11, fontWeight: 700, letterSpacing: '0.12em', textTransform: 'uppercase', color: 'var(--gold)', marginBottom: 14 }}>What We're Looking For</div>
                <ul style={{ listStyle: 'none', padding: 0, margin: '0 0 32px', display: 'flex', flexDirection: 'column', gap: 9 }}>
                  {requirements.map(r => (
                    <li key={r} style={{ display: 'flex', alignItems: 'flex-start', gap: 8, fontSize: 13, color: '#4A6A84', lineHeight: 1.65 }}>
                      <span style={{ color: 'var(--gold)', fontWeight: 700, fontSize: 15, lineHeight: 1.35, flexShrink: 0 }}>✓</span>
                      {r}
                    </li>
                  ))}
                </ul>

                {/* Apply box — same style as Services fee/CTA row */}
                <div style={{ background: 'var(--navy-dark)', borderRadius: 10, padding: '24px 24px', border: '1px solid rgba(201,168,76,0.2)' }}>
                  <div style={{ fontSize: 13, fontWeight: 700, color: 'var(--gold)', marginBottom: 8 }}>Ready to apply?</div>
                  <p style={{ fontSize: 12, color: 'rgba(255,255,255,0.5)', lineHeight: 1.7, margin: '0 0 14px' }}>
                    Send your resume + examples of social accounts, videos, Reels, or TikToks you've created. Tell us why you're interested in automotive content.
                  </p>
                  <div style={{ background: 'rgba(255,255,255,0.06)', borderRadius: 6, padding: '10px 14px', marginBottom: 14, display: 'flex', alignItems: 'center', gap: 8 }}>
                    <span style={{ fontSize: 11, color: 'rgba(255,255,255,0.35)', flexShrink: 0 }}>Send to:</span>
                    <span style={{ fontSize: 12, fontWeight: 700, color: 'white', letterSpacing: '0.01em' }}>veridriveus@gmail.com</span>
                  </div>
                  <a
                    href="mailto:veridriveus@gmail.com?subject=Application%20%E2%80%94%20Social%20Media%20%26%20Content%20Coordinator&body=Hi%20VeriDrive%20team%2C%0A%0AI'm%20applying%20for%20the%20Social%20Media%20%26%20Content%20Coordinator%20role.%0A%0APortfolio%2Flinks%3A%0A%0AWhy%20I'm%20interested%3A%0A%0AHow%20I'd%20grow%20VeriDrive%3A"
                    className="btn-gold"
                    style={{ fontSize: 12, padding: '11px 20px', width: '100%', justifyContent: 'center' }}
                  >
                    Open Email App →
                  </a>
                  <p style={{ fontSize: 11, color: 'rgba(255,255,255,0.25)', textAlign: 'center', margin: '10px 0 0', lineHeight: 1.5 }}>
                    This opens your email app. If it doesn't work,<br />email us directly at veridriveus@gmail.com
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>

        <style>{`
          @media (max-width: 960px) {
            .careers-body { grid-template-columns: 1fr 1fr !important; }
          }
          @media (max-width: 600px) {
            .careers-body { grid-template-columns: 1fr !important; gap: 32px !important; }
          }
        `}</style>
      </section>
    </>
  );
}
